import { createAction } from '@reduxjs/toolkit';

import type { ProfileSelection } from '../domain/list-draft.model';

/**
 * "Signal" actions: intents the UI dispatches for the saga to act on. They are
 * deliberately kept out of the slice's reducers: they trigger side effects, not
 * state transitions (the saga owns the resulting `save*` transitions).
 */

/** Start the full multi-phase save flow with the UI-computed selection. */
export const submitRequested = createAction<ProfileSelection>('addList/submitRequested');

/** Create an empty list directly (no profiles, no stepper). */
export const createEmptyRequested = createAction('addList/createEmptyRequested');

/** Abort an in-flight save (also emitted implicitly by re-submitting). */
export const submitCancelled = createAction('addList/submitCancelled');

/** Compute the enrichment buckets (dispatched when entering the confirm step). */
export const bucketsRequested = createAction('addList/bucketsRequested');

/**
 * Analyse a text into a filter tree: the scope, on the way into select, or what
 * the admin typed in the step's AI search bar (board E3).
 */
export const scopeAnalyzeRequested = createAction<string | undefined>(
  'addList/scopeAnalyzeRequested',
);

/**
 * Parse the file dropped on the embedded import step. The `File` payload is
 * deliberately non-serialisable (listed in the store's
 * `serializableCheck.ignoredActions`); the saga consumes it for the upload
 * call and only its name enters the state.
 */
export const fileParseRequested = createAction<File>('addList/fileParseRequested');

/** Stop the upload in flight (the file card's Cancel). */
export const fileUploadCancelRequested = createAction('addList/fileUploadCancelRequested');

/** Dry-run the mapped rows of the embedded import step. */
export const fileAnalyzeRequested = createAction('addList/fileAnalyzeRequested');

/** Load the licence dropdown options (dispatched when entering the details step). */
export const licencesRequested = createAction('addList/licencesRequested');

/** A licence was created from the picker (#597): reload the licences and pick it. */
export const licenceCreated = createAction<string>('addList/licenceCreated');

/** Load the source-list dropdown of the import step. */
export const sourceListsRequested = createAction('addList/sourceListsRequested');

/** Import every profile of the chosen source list (then check duplicates). */
export const listImportRequested = createAction<{ listId: string; listName: string }>(
  'addList/listImportRequested',
);

/** (Re)fetch the imported-profiles preview page (ids/page/pageSize live in state). */
export const importPreviewRequested = createAction('addList/importPreviewRequested');
